import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldCheck, Check } from 'lucide-react';
import logo from '@/assets/biolyn-logo.png';

const STORAGE_KEY = 'biolyn_gate_accepted';

const terms = [
  'I am at least 18 years of age.',
  'I represent a business, research institution, or licensed manufacturer.',
  'I understand all products are supplied for laboratory and research purposes only — not for human or veterinary consumption.',
];

export default function AgeGate() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      setOpen(false);
      return;
    }
    const accepted = (() => {
      try { return localStorage.getItem(STORAGE_KEY) === 'true'; } catch { return false; }
    })();
    setOpen(!accepted);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const accept = () => {
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch { /* localStorage unavailable */ }
    setOpen(false);
  };

  const decline = () => {
    window.location.href = 'https://www.google.com';
  };

  return (
    <div
      data-testid="age-gate"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(7,18,20,0.78)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
    >
      <div className="w-full max-w-lg bg-white border border-[#E9ECEF] shadow-[0_40px_100px_-30px_rgba(10,17,40,0.6)] animate-rise">
        <div className="p-8 md:p-10">
          <img src={logo} alt="Biolyn Pharmaceutical B.V." className="h-12 w-auto mb-6" />

          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-[#0A7C84]" strokeWidth={2} />
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#0A7C84]" style={{ fontFamily: 'var(--font-mono)' }}>
              Age Verification &amp; Research Use
            </span>
          </div>

          <h2
            className="text-2xl md:text-3xl tracking-tight text-[#0A1128] mb-3"
            style={{ fontFamily: 'var(--font-heading)', fontWeight: 900 }}
            data-testid="age-gate-heading"
          >
            Please confirm before you continue
          </h2>

          <p className="text-sm text-[#495057] leading-relaxed mb-6" style={{ fontFamily: 'var(--font-body)' }}>
            Biolyn Pharmaceutical B.V. supplies products strictly to businesses and institutions for laboratory and research use. Our products are not for human or veterinary consumption.
          </p>

          <ul className="space-y-3 mb-6">
            {terms.map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-[#0A7C84]/10 flex items-center justify-center">
                  <Check className="w-3 h-3 text-[#0A7C84]" strokeWidth={3} />
                </span>
                <span className="text-sm text-[#1A2233] leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>{t}</span>
              </li>
            ))}
          </ul>

          <label className="flex items-start gap-3 mb-8 cursor-pointer select-none" data-testid="age-gate-agree-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#0A7C84] shrink-0"
              data-testid="age-gate-agree-checkbox"
            />
            <span className="text-sm text-[#495057] leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
              I have read and agree to all of the statements above.
            </span>
          </label>

          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={decline}
              className="btn-pill flex-1 px-5 py-3 border border-[#DEE2E6] text-sm text-[#495057] hover:bg-[#F8F9FA]"
              style={{ fontFamily: 'var(--font-body)', fontWeight: 600 }}
              data-testid="age-gate-decline"
            >
              Decline
            </button>
            <button
              onClick={accept}
              disabled={!agreed}
              className="btn-pill flex-1 px-5 py-3 bg-[#0A7C84] text-white text-sm hover:bg-[#075E64] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              style={{ fontFamily: 'var(--font-body)', fontWeight: 600 }}
              data-testid="age-gate-accept"
            >
              I Agree &mdash; Enter Site
            </button>
          </div>

          <p className="text-[10px] text-[#A0AAB2] text-center mt-5 leading-relaxed" style={{ fontFamily: 'var(--font-mono)' }}>
            By entering you accept our research-use terms. B2B only. No retail / No B2C.
          </p>
        </div>
      </div>
    </div>
  );
}
